import React, {useState, useMemo, useRef, useEffect, createContext, useCallback} from "react"
import ReactModal from "react-modal"
import {createPortal} from "react-dom"
import {classNames} from 'js/studios/shared/utils'
import {ErrorBoundary} from 'js/studios/shared/error_boundary'

// esbuild optimizes `123ms` to `.12s` in prod bundles, so we need to parse both
function parseCssTimeAsMs(val) {
  val = val?.trim?.() || ""
  if (val.endsWith("ms")) {
    return parseInt(val, 10)
  } else if (val.endsWith("s")) {
    return parseFloat(val) * 1_000
  }
}

// <dialog> based modal, intended to replace ReactModal usage
function DialogModal({
  isOpen,
  onAfterOpen,
  onRequestClose,
  className,
  contentLabel,
  closeTimeoutMS,
  shouldCloseOnOverlayClick=true,
  children,
}) {
  const
    [reallyOpen, setReallyOpen] = useState(isOpen),
    dlgRef = useRef(null),
    closeTimerRef = useRef(null),
    clearCloseTimer = () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }

  useEffect(() => {
    if (isOpen === reallyOpen) { return }
    if (isOpen) {
      setReallyOpen(isOpen)
      clearCloseTimer()
    } else if (dlgRef.current) {
      const modalTransitionMs = parseCssTimeAsMs(window.getComputedStyle(dlgRef.current).getPropertyValue("--modal-transition-time"))
      dlgRef.current.close()
      clearCloseTimer()
      closeTimerRef.current = window.setTimeout(() => setReallyOpen(isOpen), modalTransitionMs)
    }
  }, [isOpen, reallyOpen])

  useEffect(() => {
    if (reallyOpen && dlgRef.current) {
      dlgRef.current.inert = true
      dlgRef.current.showModal()
      dlgRef.current.inert = false
      onAfterOpen?.()
    }
  }, [reallyOpen, onAfterOpen])

  const
    otherProps = useMemo(() => {
      const p = {closedby: (shouldCloseOnOverlayClick ? "any" : "none")}
      if (closeTimeoutMS) {
        p.style = {"--modal-transition-time": `${closeTimeoutMS}ms`}
      }
      if (contentLabel) {
        p["aria-label"] = contentLabel
      }
      return p
    }, [shouldCloseOnOverlayClick, closeTimeoutMS, contentLabel]),
    // `closedby=any` allows bg clicks to close on desktop automatically (it triggers a cancel
    // event), but iOS doesn't support that, so we still bind a click handler for bg clicks
    onClick = ev => {
      if (shouldCloseOnOverlayClick && dlgRef.current && ev.target === dlgRef.current) {
        const rect = ev.target.getBoundingClientRect(),
          isBgClick = (ev.clientY < rect.top || ev.clientY > rect.bottom ||
            ev.clientX < rect.left || ev.clientX > rect.right)
        if (isBgClick) {
          onRequestClose(ev)
        }
      }
    },
    onCancel = ev => {
      ev.preventDefault()
      onRequestClose(ev)
    }

  return <dialog ref={dlgRef} onCancel={onCancel} onClick={onClick} className={classNames("modal", className)}
    {...otherProps}
    >
    {reallyOpen ? children : null}
  </dialog>
}

// old ReactModal base modal, this is effectively the AdminModal at this point
export class Modal extends React.Component {
  componentDidMount() {
    ReactModal.setAppElement('body')
  }

  render() {
    let closeButton = null
    if (this.props.closeButton === true) {
      closeButton = <a className="icon-cancel cancel" onClick={this.props.onRequestClose}></a>
    }

    return <ErrorBoundary>
      <ReactModal
        isOpen={this.props.isOpen}
        onAfterOpen={this.props.onAfterOpen}
        onRequestClose={this.props.onRequestClose}
        closeTimeoutMS={300}
        overlayClassName={classNames('dialog_full modal_bg_new', {open: this.props.isOpen, [this.props.overlayClassName]: this.props.overlayClassName})}
        contentLabel={this.props.contentLabel}
        className={classNames('modal_container', this.props.className)}
        shouldCloseOnOverlayClick={this.props.shouldCloseOnOverlayClick}
      >
        {closeButton}
        {this.props.children}
      </ReactModal>
    </ErrorBoundary>
  }
}

export const AdminModalCtx = createContext(null)
AdminModalCtx.displayName = "AdminModalCtx"

export function AdminModal({
  isOpen,
  onAfterOpen,
  onRequestClose: onRequestCloseProp,
  unsavedChangesPrompt="There are unsaved changes. Are you sure you want to close?",
  clearUnsavedOnClose=true,
  shouldCloseOnOverlayClick=false,
  className,
  contentLabel,
  children
}) {
  const
    unsavedChangesRef = useRef(false),
    [unsavedChangesState, setUnsavedChangesState] = useState(unsavedChangesRef.current),
    setHasUnsavedChanges = useCallback(v => {
      unsavedChangesRef.current = v
      setUnsavedChangesState(v)
    }, []),
    requestClose = useCallback(ev => {
      ev?.preventDefault?.()
      if (!unsavedChangesRef.current || window.confirm(unsavedChangesPrompt)) {
        if (clearUnsavedOnClose) {
          setHasUnsavedChanges(false)
        }
        onRequestCloseProp(ev)
      }
    }, [onRequestCloseProp, unsavedChangesPrompt, clearUnsavedOnClose, setHasUnsavedChanges]),
    ctxVal = useMemo(() => ({
      hasUnsavedChanges: unsavedChangesState,
      setHasUnsavedChanges, requestClose,
    }), [unsavedChangesState, setHasUnsavedChanges, requestClose])

  return <ErrorBoundary>
    <AdminModalCtx.Provider value={ctxVal}>
      {createPortal(
        <DialogModal
          isOpen={isOpen}
          onAfterOpen={onAfterOpen}
          onRequestClose={requestClose}
          contentLabel={contentLabel}
          className={className}
          shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
        >
          {children}
          <button className="modal__close" type="button" aria-label="Close modal" tabIndex="0" onClick={requestClose}>
          </button>
        </DialogModal>,
        document.body)}
    </AdminModalCtx.Provider>
  </ErrorBoundary>
}

export function ConsumerModal({
  isOpen,
  onAfterOpen,
  onRequestClose,
  className,
  contentLabel,
  closeTimeoutMS,
  children,
}) {
  return <ErrorBoundary>
    <DialogModal
      isOpen={isOpen}
      onAfterOpen={onAfterOpen}
      onRequestClose={onRequestClose}
      closeTimeoutMS={closeTimeoutMS}
      className={className}
      contentLabel={contentLabel}
    >
      {children}
      <button className="modal__close btn btn--icon btn--sm" type="button" aria-label="Close modal" tabIndex="0" onClick={onRequestClose}>
        <span className="icon icon--x"></span>
      </button>
    </DialogModal>
  </ErrorBoundary>
}

const ScrollModalCtx = createContext(null)
ScrollModalCtx.displayName = "ScrollModalCtx"

// use this component as the first child of a DialogModal when contents may be too long and
// require scrolling. Provides a context with access to the scroll container div so modals
// can manually manage scrolling as needed.
export function ModalScrollContent({children}) {
  const
    scrollDivRef = useRef(),
    ctxVal = useMemo(() => ({
      scrollDivRef: scrollDivRef,
      scrollToBottom: () => scrollDivRef.current?.scrollTo?.(
        {top: scrollDivRef.current?.scrollHeight, behavior: "smooth"}
      ),
    }), [])

  return <div className="modal-scroll-content">
    <div ref={scrollDivRef}>
      <ScrollModalCtx.Provider value={ctxVal}>{children}</ScrollModalCtx.Provider>
    </div>
  </div>
}
ModalScrollContent.Ctx = ScrollModalCtx
